<template>
  <b-row>
    <b-col sm="12">
      <b-card header-tag="header" footer-tag="footer">
        <b-row>
          <b-col>
            <h4 class="card-title mb-0">Registro de Cambios</h4>
            <div class="small text-muted">Historial de cambios y mejoras realizadas en el sistema</div>
          </b-col>
        </b-row>
      </b-card>

      <b-row>
        <b-col lg="9">

          <!-- Filtros -->
          <b-card class="mb-3">
            <b-row>
              <b-col md="4">
                <b-form-group label="Módulo" label-size="sm">
                  <b-form-select v-model="filter.module" size="sm" @change="applyFilters()">
                    <b-form-select-option value="">Todos los módulos</b-form-select-option>
                    <b-form-select-option
                      v-for="mod in modules"
                      :key="mod.key"
                      :value="mod.key">
                      {{ mod.name }}
                    </b-form-select-option>
                  </b-form-select>
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Desde" label-size="sm">
                  <b-form-input type="date" size="sm" v-model="filter.dateFrom" @change="applyFilters()"></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="3">
                <b-form-group label="Hasta" label-size="sm">
                  <b-form-input type="date" size="sm" v-model="filter.dateTo" @change="applyFilters()"></b-form-input>
                </b-form-group>
              </b-col>
              <b-col md="2" class="d-flex align-items-end pb-3">
                <b-button size="sm" variant="outline-secondary" @click="clearFilters()" block>
                  Limpiar
                </b-button>
              </b-col>
            </b-row>
          </b-card>

          <!-- Listado agrupado por fecha -->
          <div v-if="groupedChanges.length">
            <div v-for="group in groupedChanges" :key="group.date" class="mb-3">
              <h6 class="text-muted mb-2">
                <b-icon icon="calendar3" class="mr-1"></b-icon>
                {{ formatDate(group.date) }}
              </h6>

              <b-list-group>
                <b-list-group-item
                  v-for="(item, index) in group.items"
                  :key="index"
                  class="changelog-item"
                  :class="{ 'changelog-item-new': isRecent(item) }">

                  <div class="d-flex w-100 justify-content-between align-items-start">
                    <div>
                      <p class="mb-0">{{ item.description }}</p>
                    </div>
                    <b-badge :variant="isGeneralChange(item) ? 'danger' : 'dark'" pill class="ml-2" style="white-space: nowrap;">
                      {{ item.module_name }}
                    </b-badge>
                  </div>
                </b-list-group-item>
              </b-list-group>
            </div>
          </div>

          <b-alert v-else show variant="info">
            No se encontraron cambios con los filtros seleccionados.
          </b-alert>

        </b-col>

        <!-- Sidebar: resumen por módulo -->
        <b-col lg="3">
          <b-card no-body>
            <b-card-header header-bg-variant="dark" header-text-variant="white" class="p-2">
              <small class="font-weight-bold">Resumen por módulo</small>
            </b-card-header>
            <b-list-group flush>
                <b-list-group-item
                  v-for="mod in moduleSummary"
                  :key="mod.key"
                  href="#"
                  class="d-flex justify-content-between align-items-center p-2"
                  :class="{ 'module-summary-new': moduleHasRecent(mod.key) }"
                  @click="filterByModule(mod.key)">
                <small>{{ mod.name }}</small>
                <b-badge variant="dark" pill>{{ mod.count }}</b-badge>
              </b-list-group-item>
            </b-list-group>
          </b-card>

          <b-card class="mt-3 text-center" no-body>
            <b-card-body class="p-3">
              <b-row>
                <b-col cols="6" class="border-right">
                  <h2 class="mb-0">{{ currentMonthChanges }}</h2>
                  <small class="text-muted">mes en curso</small>
                </b-col>
                <b-col cols="6">
                  <h2 class="mb-0">{{ allChanges.length }}</h2>
                  <small class="text-muted">cambios totales</small>
                </b-col>
              </b-row>
            </b-card-body>
          </b-card>
        </b-col>
      </b-row>

    </b-col>
  </b-row>
</template>

<script>
  import Session from '@/handler/session'
  import changelogData from '@/components/changelog/data.json'

  export default {
    data: () => {
      return {
        primaryColor: '',
        filter: {
          module: '',
          dateFrom: '',
          dateTo: ''
        },
        allChanges: [],
        filteredList: []
      }
    },
    computed: {
      modules() {
        var map = {}
        this.allChanges.forEach(item => {
          if (!map[item.module]) {
            map[item.module] = item.module_name
          }
        })

        var result = []
        for (var key in map) {
          result.push({ key: key, name: map[key] })
        }
        return result.sort((a, b) => a.name.localeCompare(b.name))
      },
      groupedChanges() {
        var groups = {}
        this.filteredList.forEach(item => {
          if (!groups[item.date]) {
            groups[item.date] = []
          }
          groups[item.date].push(item)
        })

        var result = []
        var dates = Object.keys(groups).sort((a, b) => b.localeCompare(a))
        dates.forEach(date => {
          result.push({ date: date, items: groups[date] })
        })
        return result
      },
      moduleSummary() {
        var map = {}
        this.allChanges.forEach(item => {
          if (!map[item.module]) {
            map[item.module] = { key: item.module, name: item.module_name, count: 0, lastDate: item.date }
          }
          map[item.module].count++
          if (item.date > map[item.module].lastDate) {
            map[item.module].lastDate = item.date
          }
        })

        var result = []
        for (var key in map) {
          result.push(map[key])
        }
        return result.sort((a, b) => {
          if (b.lastDate === a.lastDate) {
            return b.count - a.count
          }
          return b.lastDate.localeCompare(a.lastDate)
        })
      },
      currentMonthChanges() {
        var today = new Date()
        var year = today.getFullYear()
        var month = today.getMonth() + 1

        return this.allChanges.filter(item => {
          if (!item || !item.date) {
            return false
          }
          var parts = item.date.split('-')
          return parseInt(parts[0]) === year && parseInt(parts[1]) === month
        }).length
      }
    },
    mounted() {
      this.setup()
      this.loadChangelog()
    },
    methods: {
      setup() {
        if (Session.getSession().settings) {
          this.primaryColor = Session.getSession().settings.color_primary
          this.createCSS()
        }
      },
      createCSS() {
        var hoja = document.createElement('style')
        hoja.innerHTML = ".list-group-item-action:hover, .list-group-item-action:focus, .list-group-item-action.active { background-color: " + this.primaryColor + "; color: #fff !important; }"
        document.body.appendChild(hoja)
      },
      getUserModules() {
        var modules = []
        if (Session.getSession().auth && Session.getSession().auth.user.permissions) {
          var menu = Session.getSession().auth.user.permissions.menu
          for (var prop in menu) {
            modules.push(menu[prop].router)
          }
        }
        return modules
      },
      loadChangelog() {
        var userModules = this.getUserModules()
        var data = changelogData.slice()

        if (userModules.length) {
          data = data.filter(item => item.module === 'general' || userModules.indexOf(item.module) !== -1)
        }

        this.allChanges = data.sort((a, b) => b.date.localeCompare(a.date))
        this.filteredList = this.allChanges.slice()

        // Si viene con query param de módulo, filtrar
        if (this.$route.query.module) {
          this.filter.module = this.$route.query.module
          this.applyFilters()
        }
      },
      applyFilters() {
        var data = this.allChanges.slice()

        if (this.filter.module) {
          data = data.filter(item => item.module === this.filter.module)
        }
        if (this.filter.dateFrom) {
          data = data.filter(item => item.date >= this.filter.dateFrom)
        }
        if (this.filter.dateTo) {
          data = data.filter(item => item.date <= this.filter.dateTo)
        }

        this.filteredList = data
      },
      clearFilters() {
        this.filter.module = ''
        this.filter.dateFrom = ''
        this.filter.dateTo = ''
        this.filteredList = this.allChanges.slice()
      },
      filterByModule(moduleKey) {
        this.filter.module = moduleKey
        this.applyFilters()
      },
      formatDate(dateStr) {
        var parts = dateStr.split('-')
        var months = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                      'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
        var monthIndex = parseInt(parts[1]) - 1
        return parts[2] + ' de ' + months[monthIndex] + ', ' + parts[0]
      },
      isRecent(item) {
        var today = new Date()
        var thirtyDaysAgo = new Date(today.getTime() - (30 * 24 * 60 * 60 * 1000))
        var limitDate = thirtyDaysAgo.toISOString().substring(0, 10)
        return item && item.date >= limitDate
      },
      isGeneralChange(item) {
        return item && item.module === 'general'
      },
      moduleHasRecent(moduleKey) {
        return this.allChanges.some(item => item.module === moduleKey && this.isRecent(item))
      }
    }
  }
</script>

<style scoped>
  .changelog-item {
    border-left: 3px solid #20a8d8;
    background-color: #f3f9ff;
  }
  .changelog-item-new {
    border-left-color: #ffcc6a;
    background-color: #fff8e1;
  }
  .module-summary-new {
    background-color: #fff8e1;
  }
</style>
